import { inject, Injectable } from '@angular/core'

import { MessageService } from 'primeng/api'
import { Subject } from 'rxjs'

import { ApiErrors, Driver } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { BASE_64_REGEX } from '@ti/core/common/constants'
import { DeviceService, ErrorHandler } from '@ti/core/common/services'
import { DIALOG_FACADE } from '@ti/core/contracts/dialog'
import { LanguageService } from '@ti/core/ui-kit/i18n'

@Injectable({ providedIn: 'root' })
export class DriversManager {
  public readonly updated$ = new Subject<string[]>()

  protected readonly api = inject(ApiService)
  protected readonly errorHandler = inject(ErrorHandler)
  protected readonly languageService = inject(LanguageService)
  protected readonly messageService = inject(MessageService)
  protected readonly dialogFacade = inject(DIALOG_FACADE)
  protected readonly device = inject(DeviceService)

  public async getById(driverId: string) {
    return this.api.fleet.driver.getById(driverId)
  }

  public async create(data: Driver) {
    try {
      const { photo, ...payload } = this.normalizeDismissedDate(data)
      const result = await this.api.fleet.driver.create(payload)

      if (photo) {
        await this.setDriverPhoto(result, photo)
      }

      const labels = await this.languageService.massTranslate(
        {
          detail: 'fleet-settings.drivers.created-message.detail',
        },
        { name: data.name || '' },
      )
      this.messageService.add({ ...labels, life: 3000, severity: 'success' })

      this.updated$.next([])
      return true
    } catch (error) {
      if ((error as Record<string, any>)?.message === ApiErrors.driverWithTheSameRFIDTagIdExists) {
        throw error
      } else {
        await this.errorHandler.notifyUser(error, `Cannot create driver`)
      }
    }
    return false
  }

  public async update(driver: Driver, data: Partial<Driver>) {
    try {
      const { photo, ...payload } = this.normalizeDismissedDate(data)

      await this.api.fleet.driver.update(driver.id, payload)

      if (photo && driver.photo !== photo) {
        await this.setDriverPhoto(driver, photo)
      } else if (!photo && driver.photo) {
        await this.removeDriverPhoto(driver)
      }

      const labels = await this.languageService.massTranslate(
        {
          detail: 'fleet-settings.drivers.updated-message.detail',
        },
        { name: data.name || '' },
      )
      this.messageService.add({ ...labels, life: 3000, severity: 'success' })
      this.updated$.next([driver.id])

      return true
    } catch (error) {
      if ((error as Record<string, any>)?.message === ApiErrors.driverWithTheSameRFIDTagIdExists) {
        throw error
      } else {
        await this.errorHandler.notifyUser(error, `Cannot update driver`)
      }
    }
    return false
  }

  public async delete(id: string, data: Driver) {
    const dialogResult = await this.confirmDelete(data.name, data.isActive)
    if (!dialogResult) return

    let result: boolean
    switch (dialogResult) {
      case 'sub-action': {
        result = await this.deactivate(id, data)
        break
      }
      case true: {
        result = await this.deleteDriver(id, data)
        break
      }
    }

    this.updated$.next([id])
    return result
  }

  public async deactivate(id: string, data: Driver) {
    try {
      await this.api.fleet.driver.update(id, { isActive: false })
      const labels = await this.languageService.massTranslate(
        {
          detail: 'fleet-settings.drivers.deactivated-message.detail',
        },
        { name: data.name || '' },
      )
      this.messageService.add({ ...labels, life: 3000, severity: 'success' })

      return true
    } catch (error) {
      await this.errorHandler.notifyUser(error, `Cannot deactivate driver`)
    }
    return false
  }

  protected normalizeDismissedDate(data: Partial<Driver>) {
    if (data.dismissedAt) {
      const local = new Date(data.dismissedAt)
      // build a NEW Date at UTC midnight for that same y/m/d
      data.dismissedAt = new Date(
        Date.UTC(local.getFullYear(), local.getMonth(), local.getDate(), 12, 0, 0, 0),
      )
    }

    return data
  }

  protected async deleteDriver(id: string, data: Driver) {
    try {
      await this.api.fleet.driver.remove(data.id)
      const labels = await this.languageService.massTranslate(
        {
          detail: 'fleet-settings.drivers.deleted-message.detail',
        },
        { name: data.name || '' },
      )
      this.messageService.add({ ...labels, life: 3000, severity: 'success' })

      return true
    } catch (error) {
      await this.errorHandler.notifyUser(error, `Cannot delete driver`)
    }
    return false
  }

  protected async setDriverPhoto(driver: Driver, photo: string) {
    if (photo && photo.includes('base64')) {
      const base64UrlMatch = photo.match(BASE_64_REGEX)
      if (base64UrlMatch) {
        await this.api.fleet.driver.setDriverPhotoBase64(
          driver.id,
          base64UrlMatch[2],
          base64UrlMatch[1],
        )
      }
    }
  }

  protected async removeDriverPhoto(driver: Driver) {
    return this.api.fleet.driver.removeDriverPhoto(driver.id)
  }

  protected async confirmDelete(name: string, subAction: boolean) {
    const labels = await this.languageService.massTranslate(
      {
        summary: 'fleet-settings.drivers.delete-dialog.summary',
        description: 'fleet-settings.drivers.delete-dialog.description',
        confirmButton: this.device.isMobile() ? 'button.delete' : 'common.yes-delete',
        subActionButton: subAction ? 'fleet-settings.drivers.delete-dialog.sub-action-button' : '',
        cancelButton: 'button.cancel',
      },
      { name },
    )
    return this.dialogFacade.confirmWithSubAction({
      ...labels,
      confirmButtonColor: 'var(--color-alert-500)',
      confirmButtonIcon: this.device.isMobile() ? '' : 'pi-trash',
    })
  }
}
